/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuButton, NeuTextarea } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import NotesCard from './sideDrawerCards/NotesCard';
import PastRoundCard from './sideDrawerCards/PastRoundsCard/PastRoundCard';
import PastTasksCard from './sideDrawerCards/PastTasksCard';

import {
  openModal,
  setModalData,
  setModalType,
  setModalAction,
  getRound,
  selectDataForEdit,
  setToastType
} from '../../../redux/actions';
import {
  usePostAddEmployeeNoteMutation,
  usePostRemoveEmployeeNoteMutation
} from '../../../redux/apis/Employees.api';
import {
  usePostAddPatientNoteMutation,
  usePostRemovePatientNoteMutation
} from '../../../redux/apis/Patients.api';

import { logRoundProfileClick } from '../../../utils/analyticsHelpers';
import { date } from '../../../utils/helpers';

import useToast from '../../../services/Toast';
import { useUserRolesRef } from '../../../services/UserRoles/userRoles';

import {
  AuthUserProps,
  Feedback,
  Issue,
  RoundHistory
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CardsContainer,
  DrawerContainer,
  HorizontalDivider,
  NoteForm,
  NotesContainer,
  NoteCardsContainer,
  RoundCardContainer,
  SaveNoteButton,
  TaskCardContainer
} from './HistorySideDrawer.styles';

/**
 * Constant Imports
 */
import serviceCentralCategories from '../../../constants/serviceCentralCategories.json';

interface HistorySideDrawerProps {
  activePastRoundId?: string;
  authorizedUser?: AuthUserProps;
  facilities: any;
  roundHistory?: RoundHistory[];
  selectedTab: string;
  selectedPerson?: any;
  supportType?: string;
  tasks?: (Issue | Feedback)[];
  type: string;
  setSelectedTab: Dispatch<SetStateAction<string>>;
}

const HistorySideDrawer: FC<HistorySideDrawerProps> = ({
  activePastRoundId,
  authorizedUser,
  facilities,
  roundHistory = [],
  selectedTab,
  selectedPerson,
  supportType,
  tasks,
  type,
  setSelectedTab
}) => {
  const dispatch = useDispatch();

  const [noteMessage, setNoteMessage] = useState<string>('');
  const [pastIssues, setPastIssues] = useState<any>([]);
  const [pastFeedbacks, setPastFeedbacks] = useState<any>([]);

  const { isEnterpriseAdmin } = useUserRolesRef();
  const { toast } = useToast();

  const { logTrackingEvent } = useAnalyticsApi();

  const [
    addEmployeeNote
    // {
    //   isError: isAddEmployeeNoteError,
    //   isLoading: isAddEmployeeNoteLoading,
    //   isSuccess: isAddEmployeeNoteSuccess
    // }
  ] = usePostAddEmployeeNoteMutation();
  const [
    removeEmployeeNote
    // {
    //   isError: isRemoveEmployeeNoteError,
    //   isLoading: isRemoveEmployeeNoteLoading,
    //   isSuccess: isRemoveEmployeeNoteSuccess
    // }
  ] = usePostRemoveEmployeeNoteMutation();
  const [
    addPatientNote
    // {
    //   isError: isAddPatientNoteError,
    //   isLoading: isAddPatientNoteLoading,
    //   isSuccess: isAddPatientNoteSuccess
    // }
  ] = usePostAddPatientNoteMutation();
  const [
    removePatientNote
    // {
    //   isError: isRemovePatientNoteError,
    //   isLoading: isRemovePatientNoteLoading,
    //   isSuccess: isRemovePatientNoteSuccess
    // }
  ] = usePostRemovePatientNoteMutation();

  const sortedRoundHistory = [...roundHistory].sort((a: any, b: any) =>
    a.roundDate < b.roundDate ? 1 : -1
  );

  const handleClose = () => {
    setSelectedTab('');
  };

  const handleEditIssue = (issue: any) => {
    if (
      serviceCentralCategories.includes(issue.taskSubtype) ||
      serviceCentralCategories.includes(issue.category)
    ) {
      dispatch(setModalType('View Issue'));
    } else if (
      isEnterpriseAdmin ||
      (issue.userId.toLowerCase() === authorizedUser?.hcaid.toLowerCase() &&
        !issue.completed &&
        issue.taskDate &&
        !date.isOverOneDayOld(issue.taskDate))
    ) {
      dispatch(setModalType('Edit Issue'));
    } else {
      dispatch(setModalType('View Issue'));
    }
    dispatch(setModalData(issue));
    dispatch(openModal());
  };

  const handleEditRound = (round: any) => {
    if (round.roundId)
      dispatch(
        getRound({
          roundId: round.roundId,
          fromHistoryDrawer: true,
          type,
          ...(type === 'patient'
            ? { accountNum: round.accountNum, unitId: round.unitId }
            : {})
        })
      );
  };

  const handleEditFeedback = (feedback: any) => {
    dispatch(setModalType('Add Feedback'));
    dispatch(selectDataForEdit(feedback));

    // Edit modal
    if (
      !feedback.completed &&
      (isEnterpriseAdmin ||
        (feedback.userId.toLowerCase() ===
          authorizedUser?.hcaid.toLowerCase() &&
          feedback.taskDate &&
          !date.isOverOneDayOld(feedback.taskDate)))
    ) {
      dispatch(setModalAction('EDIT'));
      dispatch(openModal());
      // Partial edit modal
    } else if (
      !feedback.completed &&
      feedback.userId.toLowerCase() === authorizedUser?.hcaid.toLowerCase() &&
      feedback.taskDate &&
      date.isOverOneDayOld(feedback.taskDate)
    ) {
      dispatch(setModalAction('PARTIAL-EDIT'));
      dispatch(openModal());
    } else {
      // View modal
      dispatch(setModalAction('VIEW'));
      dispatch(openModal());
    }
  };

  const handleNoteMessageChange = (e: any) => {
    setNoteMessage(e.target.value);
  };

  const deleteNote = (note: any) => {
    dispatch(setToastType('Delete Note'));
    if (type === 'patient') {
      const removeNotePayload = {
        facilityId: authorizedUser?.facilityId ?? '',
        accountNumber: selectedPerson?.accountNum,
        noteId: note.noteId
      };
      toast(
        'Are you sure you would like to delete this note?',
        'info',
        null,
        500,
        true,
        null,
        async () => {
          await removePatientNote(removeNotePayload);
          setNoteMessage('');
        }
      );
    }
    if (type === 'employee' || type === 'validation' || type === 'support') {
      const postNotePayload = {
        userId: note.userId ?? '',
        employeeId: selectedPerson?.hcaid,
        noteId: note.noteId
      };
      toast(
        'Are you sure you would like to delete this note?',
        'info',
        null,
        500,
        true,
        null,
        async () => {
          await removeEmployeeNote(postNotePayload);
          setNoteMessage('');
        }
      );
    }
  };

  const feedbackTitle = (taskCategory: []) => {
    const extraItems = taskCategory.length - 2;
    let title;

    if (taskCategory.length > 2) {
      title = taskCategory
        .slice(0, 2)
        .toString()
        .concat(' (+', extraItems.toString(), ')');
    } else {
      title = taskCategory.toString();
    }
    return title;
  };

  const saveNote = async () => {
    if (type === 'patient' && noteMessage.trim()) {
      const postNotePayload = {
        author: `${authorizedUser?.firstName} ${authorizedUser?.lastName}`,
        facilityId: authorizedUser?.facilityId ?? '',
        accountNumber: selectedPerson?.accountNum,
        message: noteMessage
      };
      logTrackingEvent(logRoundProfileClick('save-notes'));
      await addPatientNote(postNotePayload);
    }
    if (
      (type === 'employee' || type === 'validation' || type === 'support') &&
      noteMessage.trim()
    ) {
      const postNotePayload = {
        userId: authorizedUser?.hcaid ?? '',
        employeeId: selectedPerson?.hcaid,
        author: `${authorizedUser?.firstName} ${authorizedUser?.lastName}`,
        message: noteMessage
      };
      logTrackingEvent(logRoundProfileClick('save-notes', supportType));
      await addEmployeeNote(postNotePayload);
    }
    setNoteMessage('');
  };

  const hasPastTasks = useMemo(
    () =>
      !(
        type === 'validation' ||
        (type === 'support' &&
          (supportType === 'csc' ||
            supportType === 'validation' ||
            supportType === 'aidetValidation' ||
            supportType === 'bssrValidation'))
      ),
    [type, supportType]
  );

  useEffect(() => {
    // filter out issues and feedback from tasks
    const issues = tasks
      ?.filter((task: any) => {
        return task.tasktype === 'issue' || task.taskType === 'issue';
      })
      .sort((a: any, b: any) => (a.taskDate < b.taskDate ? 1 : -1));
    const feedbacks = tasks
      ?.filter((task: any) => {
        return task.tasktype === 'feedback' || task.taskType === 'feedback';
      })
      .sort((a: any, b: any) => (a.taskDate < b.taskDate ? 1 : -1));

    setPastIssues(issues);
    setPastFeedbacks(feedbacks);
  }, [tasks]);

  const renderDrawerCard = () => {
    if (selectedTab === 'notes' && selectedPerson?.notes) {
      return (
        <NotesContainer>
          <NoteForm className="d-flex flex-column align-items-end p-2">
            <NeuTextarea
              id="Note-Text-Area"
              className="w-100"
              label="Write a Note (Do not include PHI)"
              inputMode="search"
              required
              spellCheck
              value={noteMessage}
              onNeuChange={handleNoteMessageChange}
            />
            <SaveNoteButton
              id="Save-Note"
              color="blue"
              disabled={!noteMessage.trim() ? true : undefined}
              kind="primary"
              size="small"
              onClick={() => saveNote()}
            >
              Save Note
            </SaveNoteButton>
          </NoteForm>
          <HorizontalDivider />

          <NoteCardsContainer>
            {[...(selectedPerson?.notes ?? [])]
              ?.sort((a: any, b: any) => (a.timestamp < b.timestamp ? 1 : -1))
              .map((note: any) => {
                let formattedDate;
                if (note.timestamp?.includes('T')) {
                  const dateSplit = note.timestamp?.split('T')[0].split('-');
                  formattedDate = `${dateSplit[1]}/${dateSplit[2]}/${dateSplit[0]}`;
                } else {
                  formattedDate = `${note.timestamp.slice(
                    4,
                    6
                  )}/${note.timestamp.slice(6, 8)}/${note.timestamp.slice(
                    0,
                    4
                  )}`;
                }
                return (
                  <NotesCard
                    id={`Notes-Card-${note.noteId}`}
                    key={note.noteId}
                    noteId={note.noteId}
                    date={formattedDate || ''}
                    author={note.author}
                    message={note.message}
                    deleteNote={() => deleteNote(note)}
                  />
                );
              })}
          </NoteCardsContainer>
        </NotesContainer>
      );
    }

    if (selectedTab === 'pastRounds') {
      return (
        <CardsContainer className="pt-0 pb-3">
          {sortedRoundHistory?.map((round: any) => {
            let roundDateCorrected = round.roundDate;
            if (roundDateCorrected.length < 14) {
              roundDateCorrected = roundDateCorrected.padEnd(14, '0');
            }
            const formattedRoundDate =
              date.dateTimeCombination(roundDateCorrected);
            const facilityObj = facilities?.find(
              (facility: any) => facility.facilityId === round.facilityId
            );

            return (
              <RoundCardContainer
                onClick={() => handleEditRound(round)}
                onKeyDown={() => handleEditRound(round)}
                aria-hidden
                key={round.roundId}
                active={round.roundId === activePastRoundId}
                activeColor="#EFF8FD"
              >
                <PastRoundCard
                  key={round.roundId}
                  roundId={round.roundId}
                  dateTime={
                    formattedRoundDate === 'Unknown'
                      ? formattedRoundDate
                      : formattedRoundDate.replace(', ', ' @ ').toLowerCase()
                  }
                  name={`${round.userFirstName} ${round.userLastName}`}
                  userId={round.userId}
                  facility={facilityObj?.facility}
                  room={round.room}
                  unitId={round.unitId}
                  template={
                    type === 'employee' ||
                    type === 'validation' ||
                    type === 'support'
                      ? round.templateName || 'Unknown'
                      : undefined
                  }
                  tag={round.tags ? round.tags : 'Undefined'}
                  coachingOpportunities={
                    round.coachingOpportunities
                      ? round.coachingOpportunities
                      : undefined
                  }
                />
              </RoundCardContainer>
            );
          })}
        </CardsContainer>
      );
    }
    if (selectedTab === 'pastIssues' && hasPastTasks) {
      return (
        <CardsContainer className="pt-0 pb-3">
          {pastIssues?.map((issue: any) => {
            return (
              <TaskCardContainer
                key={issue.taskId}
                onClick={() => handleEditIssue(issue)}
                onKeyDown={() => handleEditIssue(issue)}
                aria-hidden
                activeColor="#EFF8FD"
              >
                <PastTasksCard
                  key={issue.taskId}
                  taskId={issue.taskId}
                  subtype={issue.category || issue.taskSubtype}
                  taskDate={
                    issue.taskDate
                      ? date
                          .dayOfYr(issue.taskDate)
                          .replace(', ', ' @ ')
                          .toLowerCase()
                      : 'Unknown'
                  }
                  // TODO: should we remove this altogether?
                  // task title deprecated after release on 07/26/2023 user story 22529
                  taskTitle={issue.taskDate < '20230726' ? issue.title : ''}
                  taskDescription={issue.description}
                  completed={issue.completed}
                  facility={issue.facility}
                />
              </TaskCardContainer>
            );
          })}
        </CardsContainer>
      );
    }

    if (selectedTab === 'pastFeedback' && hasPastTasks) {
      return (
        <CardsContainer className="pt-0 pb-3">
          {pastFeedbacks?.map((feedback: any) => {
            return (
              <TaskCardContainer
                key={feedback.taskId}
                onClick={() => handleEditFeedback(feedback)}
                onKeyDown={() => handleEditFeedback(feedback)}
                aria-hidden
                activeColor="#EFF8FD"
              >
                <PastTasksCard
                  key={feedback.taskId}
                  taskId={feedback.taskId}
                  subtype={
                    feedback.category || feedbackTitle(feedback.taskCategory)
                  }
                  taskDate={
                    feedback.taskDate
                      ? date
                          .dayOfYr(feedback.taskDate)
                          .replace(', ', ' @ ')
                          .toLowerCase()
                      : 'Unknown'
                  }
                  taskTitle={feedback.title}
                  taskDescription={feedback.description}
                  completed={feedback.completed}
                  facility={feedback.facility}
                />
              </TaskCardContainer>
            );
          })}
        </CardsContainer>
      );
    }
    return false; // for consistent return
  };

  return selectedTab ? (
    <DrawerContainer>
      <span
        className="d-flex justify-content-end align-items-center"
        style={{
          borderBottom: '1px solid #b0b0b0',
          height: '40px'
        }}
        role="button"
        tabIndex={0}
        onClick={handleClose}
        onKeyDown={handleClose}
      >
        <NeuButton
          aria-label="hide panel"
          color="blue"
          icon="first_page"
          kind="tertiary"
          size="small"
        >
          Hide Panel
        </NeuButton>
      </span>
      {renderDrawerCard()}
    </DrawerContainer>
  ) : null;
};

const mapReduxStateToProps = (state: any) => {
  return {
    activePastRoundId: state.RoundReducer.currentRound.roundId,
    authorizedUser: state.AuthorizedUser.authorizedUser,
    facilities: state.ConfigReducer.facilities
  };
};
export default connect(mapReduxStateToProps)(HistorySideDrawer);
